/**
 * Module dependencies.
 */

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  FormList,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { requiredRule } from 'app/core/utils/field-rules';
import { appRoutes } from 'app/routes';
import { CopyId } from 'app/components/atoms/copy-id/copy-id';
import { TFunction } from 'i18next';
import React from 'react';

/**
 * List columns.
 */

const listColumns = (translate: TFunction): ListColumns => [
  {
    title: 'ID',
    dataIndex: 'id',
    size: '110px',
    key: 'id',
    render: (item: any) => <CopyId value={item.id} />
  },
  {
    title: translate('poolInvest.users.name'),
    dataIndex: 'name',
    size: '1fr',
    key: 'name',
    render: (item: any) => item.name || item.email
  },
  {
    title: translate('poolInvest.users.email'),
    dataIndex: 'email',
    size: '1fr',
    key: 'email'
  },
  {
    title: translate('poolInvest.users.role'),
    dataIndex: 'role',
    size: '120px',
    key: 'role',
    render: (item: any) => translate(`poolInvest.roles.${item.role}`)
  },
  {
    title: translate('poolInvest.users.weight'),
    dataIndex: 'weight',
    size: '120px',
    key: 'weight'
  }
];

/**
 * Role options (owner is shown for the owner's own row but never assignable).
 */

const roleOptions = (translate: TFunction) => [
  { value: 'owner', label: translate('poolInvest.roles.owner'), disabled: true },
  { value: 'editor', label: translate('poolInvest.roles.editor') },
  { value: 'viewer', label: translate('poolInvest.roles.viewer') }
];

/**
 * Add form (email + name + weight).
 */

const addFields = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'name',
    label: translate('poolInvest.users.name'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputField',
    name: 'email',
    label: translate('poolInvest.users.email'),
    rules: undefined
  },
  {
    type: 'selectField',
    name: 'role',
    label: translate('poolInvest.users.role'),
    rules: undefined,
    options: roleOptions(translate)
  },
  {
    type: 'inputNumberField',
    name: 'weight',
    label: translate('poolInvest.users.weight'),
    rules: undefined
  }
];

/**
 * Edit form. Changing the email re-links the account, but you can't change your
 * own email, and the owner's role is fixed.
 */

const editFields = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'name',
    label: translate('poolInvest.users.name'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputField',
    name: 'email',
    label: translate('poolInvest.users.email'),
    rules: undefined,
    disabled: (values: any) => !!values.isSelf
  },
  {
    type: 'selectField',
    name: 'role',
    label: translate('poolInvest.users.role'),
    rules: undefined,
    options: roleOptions(translate),
    disabled: (values: any) => values.role === 'owner'
  }
  // Weight is set on creation; changes over time are managed through
  // participation rules (the "Participation rules" sub-menu), not here.
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: String(translate('common.labels.search'))
  }
];

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType => ({
  formFields: addFields(translate)
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType => ({
  formFields: editFields(translate)
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType => ({});

/**
 * List.
 */

const list = (translate: TFunction): CrudListType => ({
  columns: listColumns(translate),
  style: { minWidth: 500 },
  route: appRoutes.dashboard.poolInvest.projectUsers,
  key: ['poolInvestParticipants'],
  endpoint: apiEndpoints.poolInvestProjectParticipants,
  filters: listFilters(translate)
});

/**
 * Config.
 */

export function createPoolInvestUsersTemplate(translate: TFunction): CrudTemplate {
  return normalizeTemplate(translate, {
    add: add(translate),
    edit: edit(translate),
    list: list(translate),
    remove: remove()
  });
}
